import { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import type { MembershipTenant } from '@atomfit/shared';
import { filterMembershipTenants } from '../../utils/membership-tenants.js';
import { membershipsApi } from '../../api/memberships.api.js';
import { useMembershipCheckoutStore } from '../../store/membership-checkout.store.js';
import { translate } from '../../i18n/index.js';
import { BackLink, Spinner } from '../design/AtomFitUI.js';

export function MembershipTenantPicker() {
  const navigate = useNavigate();
  const plan = useMembershipCheckoutStore((state) => state.pendingPlan);
  const setSelection = useMembershipCheckoutStore((state) => state.setSelection);
  const [tenants, setTenants] = useState<MembershipTenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState('');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    membershipsApi
      .tenants()
      .then((response) => {
        if (!cancelled) setTenants(response.data.data);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  const filtered = useMemo(() => filterMembershipTenants(tenants, query), [tenants, query]);
  if (!plan) return <Navigate to="/membership" replace />;

  function choose(tenant: MembershipTenant) {
    if (!plan) return;
    setSelection({ tenantId: tenant.id, tenantName: tenant.name, plan, returnTo: 'membership' });
    void navigate('/membership-checkout');
  }

  return (
    <main className="af-screen">
      <section className="af-shell af-booking-flow-shell">
        <BackLink to="/membership">
          {translate('pages.public.gym_finder.gym_finder.back_link.membership_back')}
        </BackLink>
        <h1 style={{ fontSize: 36, fontWeight: 800, margin: '0 0 6px' }}>
          {translate('components.design.atom_fit_ui.atom_fit_nav.nav_link.membership')}
        </h1>
        <p style={{ color: 'var(--af-muted)', margin: '0 0 28px' }}>
          {translate('pages.public.gym_finder.gym_finder.p.membership_gym_select')}
        </p>
        <input
          className="af-input"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          aria-label={translate(
            'pages.public.gym_finder.gym_finder.input_input.placeholder_search',
          )}
          placeholder={translate(
            'pages.public.gym_finder.gym_finder.input_input.placeholder_search',
          )}
          style={{ marginBottom: 28 }}
        />
        {loading && (
          <div className="af-card" style={{ padding: 28 }}>
            <Spinner />
          </div>
        )}
        {error && (
          <div className="af-error">
            {translate('pages.public.gym_finder.gym_finder.div_error.error_gym')}
            <button className="af-outline-btn" onClick={() => setAttempt((value) => value + 1)}>
              {translate(
                'components.landing.pricing_section.membership_pricing.button_outline_btn.retry',
              )}
            </button>
          </div>
        )}
        {!loading && !error && (
          <div className="af-gym-list">
            {filtered.map((tenant) => (
              <article key={tenant.id} className="af-card">
                <button
                  className="af-gym-row"
                  onClick={() => choose(tenant)}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    background: 'transparent',
                    border: 0,
                    color: 'inherit',
                    cursor: 'pointer',
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 800, fontSize: 17, marginBottom: 7 }}>
                      {tenant.name}
                    </div>
                    <p style={{ color: 'var(--af-muted)', fontSize: 13 }}>
                      {translate(
                        'pages.public.membership_checkout.membership_checkout.p.membership_gym_tenant',
                      )}
                    </p>
                    <ul style={{ color: 'var(--af-muted)', fontSize: 13, paddingLeft: 18 }}>
                      {tenant.gyms.map((gym) => (
                        <li key={gym.id}>
                          {gym.name} · {gym.addressLine1}, {gym.city}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <span className="af-gym-row-arrow" aria-hidden="true" />
                </button>
              </article>
            ))}
            {filtered.length === 0 && (
              <div className="af-card" style={{ padding: 28 }}>
                {translate('pages.public.gym_finder.gym_finder.div_card.copy')}
              </div>
            )}
          </div>
        )}
      </section>
    </main>
  );
}
